import { FilterSortData } from '@shares/FilterSortData';
import {
  Button,
  Col,
  Divider,
  Form,
  Icon,
  Input,
  Layout,
  Menu,
  message,
  Modal,
  Popover,
  Row,
  Spin,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography
} from 'antd';
import { FormComponentProps } from 'antd/lib/form';
import { ColumnProps } from 'antd/lib/table';
import _ from 'lodash';
import moment from 'moment';
import numeral from 'numeral';
import { InfoList, LongTextTd, O2OCustomPaging } from 'o2o_layout';
import React, { useState } from 'react';
import { match } from 'react-router-dom';
import { useAsyncRetry } from 'react-use';
import CommonTypeProps, { ICommonPayload } from '../../../common/CommonTypeProps';
import { BIGDATA_CONSTANT } from '../../../common/constant';
import { getCurrentURL } from '../../../common/helper';
import { LinkO2O } from '../../../common/LinkO2O';
import DashboardPageLayoutV2 from '../../../layout/dashboard/DashboardPageLayoutV2';
import { ObjectModels } from '../../../redux/reducer/customFieldsReducer';
import { IActionUI, SHOW_COMMON_ERROR } from '../../../redux/reducer/uiReducer';
import { useDispatch } from '../../../redux/store';
import DrawerViewJSON from '../../../shares/DrawerViewJSON';
import { O2OFormItem } from '../../../shares/O2OFormItem';
import { ExportsHistoryDAL, TypeCriteriaExportsHistory } from './DAL';
import DistinctFieldComponent from './DistinctField';
import { ExportsHistory, TypeExportsHistory } from './model';

export const useAutoDownloadFile = () => {
  const downloadFromText = (content: string, filename: string) => {
    const csvContent = content.charCodeAt(0) === 0xfeff ? content : '\uFEFF' + content;
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  };

  const downloadFromUrl = (url: string, filename?: string) => {
    if (!url) return;
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    if (filename) {
      a.download = filename;
    }
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const autoDownloadFile = (contentOrUrl: string, filename: string) => {
    if (
      typeof contentOrUrl === 'string' &&
      (contentOrUrl.startsWith('http://') || contentOrUrl.startsWith('https://'))
    ) {
      downloadFromUrl(contentOrUrl, filename);
    } else {
      downloadFromText(contentOrUrl, filename);
    }
  };

  return {
    autoDownloadFile,
    downloadFromText,
    downloadFromUrl,
  };
};

export const ButtonDowloadLink = (props: {
  commonActionFromDALV2: any;
  id: string;
  onSuccess: (id: string) => void;
}) => {
  // const [showModalTOTP, setShowModalTOTP] = useState<boolean>(false);

  const [loading, setLoading] = useState(false);
  const dispatch = useDispatch();
  const { } = useAutoDownloadFile();

  const executeOldFlow = (exportedUrl: string) => {
    props.onSuccess(exportedUrl);
    setLoading(false);
  };

  const onGetLinkDowload = async () => {
    setLoading(true);

    try {
      // 1. Lấy thông tin export từ API cũ
      const responseOld = await props.commonActionFromDALV2(
        ExportsHistoryDAL.getLinkDowload,
        props.id,
      );

      const exportedUrl = _.get(responseOld, 'result.exportedUrl', '');
      const fileName = _.get(responseOld, 'result.fileName', '');
      const securityKey = _.get(responseOld, 'result.securityKey', '');

      // 2. Gọi API V2
      const responseV2 = await props.commonActionFromDALV2(
        ExportsHistoryDAL.getLinkDowloadV2,
        props.id,
        securityKey,
      );


      // Lưu ý: Tùy commonActionFromDALV2 có unwrap response hay không
      const blob: Blob = responseV2?.data ?? responseV2;

      if (!(blob instanceof Blob)) {
        throw new Error('Invalid download response');
      }

      // 3. Kiểm tra response rỗng
      if (blob.size === 0) {
        executeOldFlow(exportedUrl);
        return;
      }

      // 4. Kiểm tra JSON response
      const contentType = blob.type || '';

      const isJson =
        contentType.includes('application/json') ||
        contentType.includes('application/problem+json');

      if (isJson) {
        const text = await blob.text();
        const parsedData = JSON.parse(text);

        // Backend yêu cầu sử dụng flow cũ
        if (
          parsedData?.useDefault === true ||
          parsedData?.data?.useDefault === true
        ) {
          executeOldFlow(exportedUrl);
          return;
        }

        // Authentication thất bại
        if (parsedData?.success === false) {
          message.error(
            parsedData.message || 'Incorrect authentication code. Please try again.',
          );
          return;
        }

        // JSON không thuộc các trường hợp đã biết
        throw new Error(parsedData?.message || 'Unexpected JSON response');
      }

      // 5. Tải CSV Blob
      const objectUrl = window.URL.createObjectURL(blob);

      const link = document.createElement('a');
      link.href = objectUrl;
      link.download = fileName || `${props.id}.csv`;

      document.body.appendChild(link);
      link.click();
      link.remove();

      setTimeout(() => {
        window.URL.revokeObjectURL(objectUrl);
      }, 1000);

      // setShowModalTOTP(false);

    } catch (error) {
      dispatch({
        type: SHOW_COMMON_ERROR,
        commonError: error,
      } as IActionUI);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button style={{ paddingLeft: 0 }} type='link' size='small' onClick={onGetLinkDowload} loading={loading}>
      Link File
    </Button>
  );
};

interface IFilterProps extends FormComponentProps {
  commonActionFromDALV2: any;
  loading: boolean;
  onSearch: (value: any) => void;
}

const FilterExportActivity = Form.create<IFilterProps>()((props: IFilterProps) => {
  const onSearch = (e: any) => {
    e.preventDefault();
    props.form.validateFieldsAndScroll((err: any, values: any) => {
      if (!err) {
        props.onSearch(values);
      }
    });
  };

  return (
    <Form colon={false} hideRequiredMark={true}>
      <Row gutter={[10, 10]} type='flex'>
        <Col sm={24} lg={12} xl={5} xxl={5} style={{ flex: 1 }}>
          <O2OFormItem label='Info'>
            {props.form.getFieldDecorator(
              'freeText',
              {}
            )(<Input.Search onPressEnter={onSearch} allowClear placeholder='Search...' />)}
          </O2OFormItem>
        </Col>
        <Col sm={24} lg={12} xl={5} xxl={5}>
          <DistinctFieldComponent
            type='category'
            commonActionFromDALV2={props.commonActionFromDALV2}
            label='Category'
            keyField='categories'
            form={props.form}
            placeholder='Select(s)'
          />
        </Col>

        <Col sm={24} lg={12} xl={5} xxl={5}>
          <DistinctFieldComponent
            type='action'
            commonActionFromDALV2={props.commonActionFromDALV2}
            label='Notif Channel'
            keyField='actions'
            form={props.form}
            placeholder='Select(s)'
          />
        </Col>

        <Col sm={24} lg={12} xl={5} xxl={5}>
          <DistinctFieldComponent
            type='source'
            commonActionFromDALV2={props.commonActionFromDALV2}
            label='Source'
            keyField='sources'
            form={props.form}
            placeholder='Select(s)'
          />
        </Col>

        <Col>
          <O2OFormItem label={<span></span>}>
            <Button loading={props.loading} type='primary' onClick={onSearch} icon='search'>
              Search
            </Button>
          </O2OFormItem>
        </Col>
      </Row>
    </Form>
  );
});

interface IExportsHistoryProps extends CommonTypeProps {
  allMoM: ObjectModels;
  getAllCustomFieldObjects: any;
  match: match<{ id: string }>
}

const TYPE_DISPATCH_CRITERIA = 'TYPE_DISPATCH_CRITERIA';

const initCriteria: ICommonPayload<TypeCriteriaExportsHistory> = {
  loading: false,
  type: TYPE_DISPATCH_CRITERIA,
  payload: {} as TypeCriteriaExportsHistory,
};

const reducerCriteria = (
  state: ICommonPayload<TypeCriteriaExportsHistory>,
  action: ICommonPayload<TypeCriteriaExportsHistory>
) => {
  switch (action.type) {
    case TYPE_DISPATCH_CRITERIA:
      return { ...state, ...action };
    default:
      throw new Error();
  }
};

const ExportsHistoryComponent = (props: IExportsHistoryProps) => {
  const [criteria, dispatchCriteria] = React.useReducer(reducerCriteria, initCriteria);
  const [visibleViewJSON, setVisibleViewJSON] = useState(false as boolean);
  const [dataFilter, setDataFilter] = useState(undefined as any);
  const [selectedRecord, setSelectedRecord] = useState(undefined as undefined | TypeExportsHistory);
  const allMoM = props.allMoM;
  const dispatch = useDispatch();

  const [isDownload, setIsDowloand] = useState<boolean>(false)

  const { } = useAutoDownloadFile();
  const lastDownloadedExportIdRef = React.useRef<string | null>(null);

  React.useEffect(() => {
    const exportId = props.match.params.id;
    if (!exportId || lastDownloadedExportIdRef.current === exportId) {
      return;
    }

    lastDownloadedExportIdRef.current = exportId;

    const handleAutoDownloadByExportId = async () => {
      setIsDowloand(true)
      try {
        // 1. Lấy thông tin export từ API cũ
        const responseOld = await props.commonActionFromDALV2(
          ExportsHistoryDAL.getLinkDowload,
          exportId,
        );

        const fileName = _.get(responseOld, 'result.fileName', '');
        const securityKey = _.get(responseOld, 'result.securityKey', '');

        // 2. Gọi API V2
        const responseV2 = await props.commonActionFromDALV2(
          ExportsHistoryDAL.getLinkDowloadV2,
          exportId,
          securityKey,
        );

        // Lưu ý: Tùy commonActionFromDALV2 có unwrap response hay không
        const blob: Blob = responseV2?.data ?? responseV2;

        if (!(blob instanceof Blob)) {
          throw new Error('Invalid download response');
        }

        // 4. Kiểm tra JSON response
        const contentType = blob.type || '';

        const isJson =
          contentType.includes('application/json') ||
          contentType.includes('application/problem+json');

        if (isJson) {
          const text = await blob.text();
          const parsedData = JSON.parse(text);

          // Backend yêu cầu sử dụng flow cũ
          if (
            parsedData?.useDefault === true ||
            parsedData?.data?.useDefault === true
          ) {
            return;
          }

          // Authentication thất bại
          if (parsedData?.success === false) {
            message.error(
              parsedData.message || 'Incorrect authentication code. Please try again.',
            );
            return;
          }

          // JSON không thuộc các trường hợp đã biết
          throw new Error(parsedData?.message || 'Unexpected JSON response');
        }

        // 5. Tải CSV Blob

        const objectUrl = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = fileName || `${exportId}.csv`;

        document.body.appendChild(link);
        link.click();
        link.remove();

        setTimeout(() => {
          window.URL.revokeObjectURL(objectUrl);
        }, 1000);
        // setShowModalTOTP(false);

      } catch (error) {
        dispatch({
          type: SHOW_COMMON_ERROR,
          commonError: error,
        } as IActionUI);
      } finally {
        setIsDowloand(false)
      }
    };

    handleAutoDownloadByExportId();
  }, [props.match.params.id]);

  const load = async () => {
    const result = await props.commonActionFromDALV2(ExportsHistoryDAL.search, criteria.payload);
    try {
      const response: ExportsHistory = await result;
      if (response.success) {
        return response;
      }
      return new ExportsHistory();
    } catch (error) {
      return new ExportsHistory();
    }
  };

  React.useEffect(() => {
    props.getAllCustomFieldObjects();
  }, []);

  React.useEffect(() => {
    props.updateMetaTitle({ title: BIGDATA_CONSTANT.trackTitle.ExportsHistory });
    const timer = setTimeout(() => {
      props.updateMeta({
        name: BIGDATA_CONSTANT.trackTitle.ExportsHistory,
        title: BIGDATA_CONSTANT.trackTitle.ExportsHistory,
        app: 'CONSOLE',
        url: getCurrentURL(props.location),
      });
    }, 5000);

    return () => {
      clearTimeout(timer);
    };
  }, []);

  React.useEffect(() => {
    dispatchCriteria({
      loading: false,
      type: TYPE_DISPATCH_CRITERIA,
      payload: { page: 0, pageSize: 10, orderBy: 'startTime:desc' } as TypeCriteriaExportsHistory,
    });
    return () => {
      dispatchCriteria({
        loading: false,
        type: TYPE_DISPATCH_CRITERIA,
        payload: {} as TypeCriteriaExportsHistory,
      });
    };
  }, []);

  const stateExportsHistory = useAsyncRetry(async () => {
    if (!_.isEmpty(criteria.payload)) {
      const data: ExportsHistory = await load();
      return data;
    }
    return new ExportsHistory();
  }, [criteria]);

  const exportsHistory = stateExportsHistory.value ? stateExportsHistory.value : new ExportsHistory();

  const columns: ColumnProps<TypeExportsHistory>[] = [
    {
      title: '#',
      key: 'index',
      sorter: false,
      width: 40,
      align: 'center',
      render: (text: any, record: TypeExportsHistory, index: number) => {
        return index + 1;
      },
    },
    {
      title: 'Download link',
      key: 'dowloadLink',
      sorter: false,
      width: 160,
      ellipsis: true,
      render: (text: any, record: TypeExportsHistory, index: number) => {
        const yesterday = moment().subtract(1, 'd');
        const isOverADayAgo = moment(record.endTime).isBefore(yesterday);
        switch (isOverADayAgo) {
          case true:
            return 'Expired';
          case false:
            return (
              <ButtonDowloadLink
                onSuccess={(link: string) => {
                  Modal.confirm({
                    title: 'Dowload',
                    content: 'Click the Download button to download your file.',
                    okText: 'Dowload',
                    cancelText: 'Close',
                    onOk: () => {
                      window.open(link);
                    },
                  });
                }}
                id={record.id || ''}
                commonActionFromDALV2={props.commonActionFromDALV2}
              />
            );
          default:
            return 'Expired';
        }
      },
    },
    {
      title: 'Start Time',
      key: 'start_time',
      width: 180,
      ellipsis: true,
      render(text: any, record: TypeExportsHistory, index: number) {
        return record.startTime ? (
          <Tooltip title={moment(record.startTime).format('DD/MM/YYYY HH:mm:ss')}>
            {moment(record.startTime).fromNow()}
          </Tooltip>
        ) : (
          '--/--'
        );
      },
    },
    {
      title: 'End Time',
      key: 'end_time',
      width: 180,
      ellipsis: true,
      render(text: any, record: TypeExportsHistory, index: number) {
        return record.startTime ? (
          <Tooltip title={moment(record.endTime).format('DD/MM/YYYY HH:mm:ss')}>
            {moment(record.endTime).fromNow()}
          </Tooltip>
        ) : (
          '--/--'
        );
      },
    },
    {
      title: 'Total',
      key: 'total',
      ellipsis: true,
      width: 120,
      render: (text: any, record: TypeExportsHistory, index: number) => {
        return (
          <Popover content={numeral(record.totalRecords).format('0,00')}>
            {numeral(record.totalRecords).format('0.[0]a')}
          </Popover>
        );
      },
    },
    {
      title: 'Status',
      key: 'status',
      sorter: false,
      width: 140,
      render: (text: any, record: TypeExportsHistory, index: number) => {
        if (record.status === 'Success') {
          return <Tag color='green'>{record.status}</Tag>;
        } else if (record.status === 'Failed') {
          return <Tag color='red'>{record.status}</Tag>;
        } else {
          return <Tag color='gold'>{record.status}</Tag>;
        }
      },
    },
    {
      title: 'Source',
      key: 'source',
      sorter: false,
      width: 250,
      ellipsis: true,
      render: (text: any, record: TypeExportsHistory, index: number) => {
        const model = allMoM.results[record.source || ''] || {};
        const { code, name } = model;
        return <LongTextTd textDisplay={name || record.source} text={code || record.source} />;
      },
    },
    {
      title: 'Reason',
      key: 'reason',
      sorter: false,
      ellipsis: true,
      width: 250,
      render: (text: any, record: TypeExportsHistory, index: number) => {
        return <LongTextTd text={record.reason} />;
      },
    },
    {
      title: 'Action',
      key: 'action',
      width: 90,
      fixed: 'right',
      render(text: any, record: TypeExportsHistory, index: number) {
        let criteria = {};
        try {
          if (record.criteria && JSON.parse(record.criteria)) {
            criteria = JSON.parse(record.criteria);
          } else {
            criteria = {};
          }
        } catch (error) {
          criteria = {};
        }
        return (
          <>
            <Tooltip title='Data Filter'>
              <Button
                size='small'
                icon='select'
                type='link'
                onClick={() => {
                  setVisibleViewJSON(true);
                  setDataFilter(criteria);
                }}
              />
            </Tooltip>

            <Divider type='vertical' />

            <Tooltip title='View Json'>
              <Button
                size='small'
                icon='eye'
                type='link'
                onClick={() => {
                  setSelectedRecord(record);
                  setVisibleViewJSON(true);
                }}
              />
            </Tooltip>
          </>
        );
      },
    },
  ];

  const renderContent = () => {
    return (
      <Row type='flex' gutter={[10, 10]}>
        <Col xs={24}>
          <Row type='flex' justify='space-between' >
            <Col xs={12}>
              <Typography.Title level={4}>Export History</Typography.Title>

            </Col>
            <Col xs={12} style={{ textAlign: 'left', width: 'full' }}>
              {isDownload && (
                <div style={{
                  display: 'flex',
                  gap: 10,
                  alignItems: 'center',
                  justifyContent: 'flex-end',
                  width: '100%',
                }}>
                  <Spin size='small' />
                  <Typography.Text type='secondary'>
                    Your export is being processed. Please wait and don't close this tab.
                  </Typography.Text>
                </div>
              )}
            </Col>
          </Row>
          <FilterExportActivity
            commonActionFromDALV2={props.commonActionFromDALV2}
            loading={stateExportsHistory.loading}
            onSearch={(values: any) => {
              dispatchCriteria({
                loading: false,
                payload: {
                  ...criteria.payload,
                  ...{ page: 0, ...values },
                },
                type: TYPE_DISPATCH_CRITERIA,
              });
            }}
          />
        </Col>



        <Col
          xs={24}
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <InfoList loading={stateExportsHistory.loading} list={exportsHistory} refresh={stateExportsHistory.retry} />
          <FilterSortData
            items={[
              {
                key: 'startTime',
                name: 'Start Time',
              },
              {
                key: 'endTime',
                name: 'End Time',
              },
              {
                key: 'totalRecords',
                name: 'Total Records',
              },
            ]}
            isLoading={stateExportsHistory.loading}
            orderBy={criteria.payload.orderBy}
            onChangeOrderBy={(sort: any) => {
              dispatchCriteria({
                loading: false,
                type: TYPE_DISPATCH_CRITERIA,
                payload: { ...criteria.payload, ...{ orderBy: sort } } as any,
              });
            }}
          />
        </Col>

        <Col xs={24}>
          <Table
            loading={stateExportsHistory.loading}
            columns={columns}
            dataSource={exportsHistory.results.content}
            rowKey='id'
            size='small'
            scroll={{ x: 'calc(700px + 50%)' }}
            pagination={false}
          />
        </Col>

        <Col xs={24}>
          <O2OCustomPaging
            list={exportsHistory}
            onChange={(e: number) => {
              dispatchCriteria({
                loading: false,
                payload: { ...criteria.payload, ...({ page: e - 1 } as TypeCriteriaExportsHistory) },
                type: TYPE_DISPATCH_CRITERIA,
              });
            }}
            onChangePageSize={(e: number) => {
              dispatchCriteria({
                loading: false,
                payload: { ...criteria.payload, ...({ page: 0, pageSize: e } as TypeCriteriaExportsHistory) },
                type: TYPE_DISPATCH_CRITERIA,
              });
            }}
          />
        </Col>

        <DrawerViewJSON
          data={dataFilter || selectedRecord}
          onClose={() => {
            setSelectedRecord(undefined);
            setDataFilter(undefined);
            setVisibleViewJSON(false);
          }}
          visible={visibleViewJSON}
        />
      </Row>
    );
  };

  const renderContentLayout = (version?: string) => {
    return version === 'v2' ? (
      <>
        <Tabs
          defaultActiveKey='activity'
          onChange={(key: string) => {
            if (key === 'my-profile') props.history.push({ pathname: '/my-account-info' });
            if (key === 'security') props.history.push({ pathname: '/security/2fa-security' });
            if (key === 'activity') props.history.push({ pathname: '/activity/invitation' });
          }}
        >
          <Tabs.TabPane tab='My Profile' key='my-profile' />
          <Tabs.TabPane tab='Security' key='security' />
          <Tabs.TabPane tab='Activity' key='activity' />
        </Tabs>
        <Layout>
          <Layout.Sider
            width={200}
            style={{ background: '#fff' }}
            className={version === 'v2' ? 'main-menu-v3 menu-connection' : ''}
          >
            <Menu mode='inline' defaultSelectedKeys={['export-history']} style={{ height: '100%' }}>
              <Menu.Item key='invitation'>
                <LinkO2O {...props} to={{ pathname: '/activity/invitation' }}>
                  Invitation
                </LinkO2O>
              </Menu.Item>
              <Menu.Item key='export-history'>
                <LinkO2O {...props} to={{ pathname: '/activity/exports-history' }}>
                  Export History
                </LinkO2O>
              </Menu.Item>
              <Menu.Item key='login-history'>
                <LinkO2O {...props} to={{ pathname: '/activity/login-history' }}>
                  Login Activity
                </LinkO2O>
              </Menu.Item>
            </Menu>
          </Layout.Sider>
          <Layout.Content style={{ overflow: 'hidden' }} className='pl30 pt10'>
            {renderContent()}
          </Layout.Content>
        </Layout>
      </>
    ) : (
      renderContent()
    );
  };

  return (
    <DashboardPageLayoutV2
      props={props}
      selectedLeftNavStandard='exports-history'
      selectedSubMenuStandard='activity'
      selectedLeftNavModern='my-info'
      selectedSubMenuModern='my-info'
      breadcrumbItemsStandard={[
        {
          title: 'Home',
          path: '/',
        },
        {
          title: 'Activity',
        },
        {
          title: 'Exports History',
        },
      ]}
      breadcrumbItemsModern={[
        { title: 'Account', path: '/' },
        { title: 'Profile', path: '/activity/invitation' },
        { title: 'Activity' },
      ]}
      pageTitle='Profile'
      renderContentLayout={renderContentLayout}
      metaTitle={props.ui.meta.title}
      pageTitleIcon={
        <Icon
          component={() => (
            <svg width='16' height='17' viewBox='0 0 16 17' fill='none' xmlns='http://www.w3.org/2000/svg'>
              <path
                d='M2.46644 16.3333H13.5335C14.8691 16.3333 15.5 15.9188 15.5 15.0228C15.5 12.7695 12.6476 9.53332 7.99663 9.53332C3.35234 9.53332 0.5 12.7695 0.5 15.0228C0.5 15.9188 1.13087 16.3333 2.46644 16.3333ZM2.13758 15.2167C1.80873 15.2167 1.68792 15.1298 1.68792 14.8891C1.68792 13.3311 3.96308 10.6566 7.99663 10.6566C12.0369 10.6566 14.3121 13.3311 14.3121 14.8891C14.3121 15.1298 14.1913 15.2167 13.8624 15.2167H2.13758ZM8.01003 8.39661C10.0436 8.39661 11.6812 6.61134 11.6812 4.39816C11.6812 2.22508 10.0436 0.5 8.01003 0.5C5.98322 0.5 4.33221 2.25183 4.33221 4.41153C4.33221 6.61803 5.9765 8.39661 8.01003 8.39661ZM8.01003 7.27998C6.64764 7.27998 5.52013 6.02295 5.52013 4.41153C5.52013 2.84691 6.64093 1.61662 8.01003 1.61662C9.37917 1.61662 10.4933 2.82686 10.4933 4.39816C10.4933 6.00958 9.3725 7.27998 8.01003 7.27998Z'
                fill='currentColor'
              />
            </svg>
          )}
        />
      }
    />
  );
};

export default ExportsHistoryComponent;
